/* @ts-self-types="./cropsize_imaging_core.d.ts" */

/**
 * Where a photo has print: 0 plain, 1 print, 2 not part of the photo (straightening fill).
 */
export class PrintMap {
    static __wrap(ptr) {
        const obj = Object.create(PrintMap.prototype);
        obj.__wbg_ptr = ptr;
        PrintMapFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        PrintMapFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_printmap_free(ptr, 0);
    }
    /**
     * @returns {Uint8Array}
     */
    get data() {
        const ret = wasm.printmap_data(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * @returns {number}
     */
    get height() {
        const ret = wasm.printmap_height(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * map pixels per photo pixel
     * @returns {number}
     */
    get scale() {
        const ret = wasm.printmap_scale(this.__wbg_ptr);
        return ret;
    }
    /**
     * @returns {number}
     */
    get width() {
        const ret = wasm.printmap_width(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) PrintMap.prototype[Symbol.dispose] = PrintMap.prototype.free;

/**
 * A frame whose pixel storage lives in WebAssembly memory.
 *
 * JavaScript copies an `ImageData` into `pixels_ptr()` once. Every operation after that
 * reads the same allocation, so repeated calls do not marshal or clone the full frame.
 */
export class RgbaFrame {
    static __wrap(ptr) {
        const obj = Object.create(RgbaFrame.prototype);
        obj.__wbg_ptr = ptr;
        RgbaFrameFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        RgbaFrameFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_rgbaframe_free(ptr, 0);
    }
    /**
     * Apply luminance CLAHE and/or a shared RGB white-point stretch in place.
     * @param {number} clip_limit
     * @param {boolean} stretch
     */
    apply_tone(clip_limit, stretch) {
        wasm.rgbaframe_apply_tone(this.__wbg_ptr, clip_limit, stretch);
    }
    /**
     * @returns {number}
     */
    estimate_skew() {
        const ret = wasm.rgbaframe_estimate_skew(this.__wbg_ptr);
        return ret;
    }
    /**
     * Extract and straighten a rotated rectangle in one bilinear resample.
     * @param {number} center_x
     * @param {number} center_y
     * @param {number} width
     * @param {number} height
     * @param {number} angle_degrees
     * @returns {RgbaFrame}
     */
    extract_rotated(center_x, center_y, width, height, angle_degrees) {
        const ret = wasm.rgbaframe_extract_rotated(this.__wbg_ptr, center_x, center_y, width, height, angle_degrees);
        return RgbaFrame.__wrap(ret);
    }
    /**
     * Fit a document's four sides around the model's outline, a quadrilateral in pixels
     * (top-left, top-right, bottom-right, bottom-left), in a photo straightened by `turn`
     * degrees: eight corner coordinates, four side kinds, four scatters.
     * @param {number} turn
     * @param {Float64Array} prior
     * @param {PrintMap} map
     * @returns {Float64Array}
     */
    fit_document(turn, prior, map) {
        const ptr0 = passArrayF64ToWasm0(prior, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        _assertClass(map, PrintMap);
        const ret = wasm.rgbaframe_fit_document(this.__wbg_ptr, turn, ptr0, len0, map.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v2;
    }
    /**
     * @param {number} width
     * @param {number} height
     */
    constructor(width, height) {
        const ret = wasm.rgbaframe_new(width, height);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        RgbaFrameFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * @returns {number}
     */
    pixels_len() {
        const ret = wasm.rgbaframe_pixels_len(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * @returns {number}
     */
    pixels_ptr() {
        const ret = wasm.rgbaframe_pixels_ptr(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * A live `Uint8Array` over this frame's WebAssembly allocation, not a copied array.
     * @returns {Uint8Array}
     */
    pixels_view() {
        const ret = wasm.rgbaframe_pixels_view(this.__wbg_ptr);
        return ret;
    }
    /**
     * Where the photo has print, as a map at most 900 pixels long; see document.rs.
     * @param {number} turn
     * @returns {PrintMap}
     */
    print_map(turn) {
        const ret = wasm.rgbaframe_print_map(this.__wbg_ptr, turn);
        return PrintMap.__wrap(ret);
    }
    /**
     * Tighten a SAM rectangle against full-resolution edges in its straightened frame.
     * @param {number} center_x
     * @param {number} center_y
     * @param {number} width
     * @param {number} height
     * @param {number} angle_degrees
     * @returns {Float64Array}
     */
    refine_rotated_rect(center_x, center_y, width, height, angle_degrees) {
        const ret = wasm.rgbaframe_refine_rotated_rect(this.__wbg_ptr, center_x, center_y, width, height, angle_degrees);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Measure each corner's rounding, starting from `standard` pixels, and whiten outside it.
     * Returns the four radii used: top-left, top-right, bottom-right, bottom-left.
     * @param {number} standard
     * @returns {Float64Array}
     */
    round_card_corners(standard) {
        const ret = wasm.rgbaframe_round_card_corners(this.__wbg_ptr, standard);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Pull a rough box onto strong straight edges, searching mostly outwards.
     * @param {number} x0
     * @param {number} y0
     * @param {number} x1
     * @param {number} y1
     * @param {number} reach
     * @param {number} prominence
     * @returns {Float64Array}
     */
    snap_edges(x0, y0, x1, y1, reach, prominence) {
        const ret = wasm.rgbaframe_snap_edges(this.__wbg_ptr, x0, y0, x1, y1, reach, prominence);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Clear crop pixels that fall outside the cleaned document mask.
     * @param {Float32Array} mask
     * @param {number} size
     * @param {number} x0
     * @param {number} y0
     * @param {number} x1
     * @param {number} y1
     * @param {number} mask_x0
     * @param {number} mask_y0
     * @param {number} mask_x1
     * @param {number} mask_y1
     */
    trim_mask(mask, size, x0, y0, x1, y1, mask_x0, mask_y0, mask_x1, mask_y1) {
        const ptr0 = passArrayF32ToWasm0(mask, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.rgbaframe_trim_mask(this.__wbg_ptr, ptr0, len0, size, x0, y0, x1, y1, mask_x0, mask_y0, mask_x1, mask_y1);
    }
    /**
     * Square up the quadrilateral top-left, top-right, bottom-right, bottom-left into a
     * new frame of the given size, with bicubic sampling.
     * @param {Float64Array} quad
     * @param {number} width
     * @param {number} height
     * @returns {RgbaFrame}
     */
    warp_quad(quad, width, height) {
        const ptr0 = passArrayF64ToWasm0(quad, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.rgbaframe_warp_quad(this.__wbg_ptr, ptr0, len0, width, height);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return RgbaFrame.__wrap(ret[0]);
    }
}
if (Symbol.dispose) RgbaFrame.prototype[Symbol.dispose] = RgbaFrame.prototype.free;

/**
 * Fill one convex silhouette around every positive component in a square mask.
 * @param {Float32Array} mask
 * @param {number} size
 * @returns {Float32Array}
 */
export function clean_mask(mask, size) {
    const ptr0 = passArrayF32ToWasm0(mask, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.clean_mask(ptr0, len0, size);
    var v2 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v2;
}

/**
 * Connected-component statistics encoded as label, x, y, width, height, area, cx, cy.
 * @param {Uint8Array} mask
 * @param {number} width
 * @param {number} height
 * @returns {Float64Array}
 */
export function connected_components(mask, width, height) {
    const ptr0 = passArray8ToWasm0(mask, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.connected_components(ptr0, len0, width, height);
    var v2 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
    return v2;
}

/**
 * @param {Float32Array} grey
 * @param {number} width
 * @param {number} height
 * @param {number} sigma
 * @param {number} radius
 * @returns {Float32Array}
 */
export function gaussian_blur(grey, width, height, sigma, radius) {
    const ptr0 = passArrayF32ToWasm0(grey, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.gaussian_blur(ptr0, len0, width, height, sigma, radius);
    var v2 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v2;
}

/**
 * @param {number} image_width
 * @param {number} image_height
 * @param {number} x0
 * @param {number} y0
 * @param {number} x1
 * @param {number} y1
 * @param {number} millimeters_per_pixel
 * @returns {Float64Array}
 */
export function measure_box(image_width, image_height, x0, y0, x1, y1, millimeters_per_pixel) {
    const ret = wasm.measure_box(image_width, image_height, x0, y0, x1, y1, millimeters_per_pixel);
    var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
    return v1;
}

/**
 * @param {Float64Array} rectangles
 * @returns {Float64Array}
 */
export function merge_rotated_rectangles(rectangles) {
    const ptr0 = passArrayF64ToWasm0(rectangles, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.merge_rotated_rectangles(ptr0, len0);
    var v2 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
    return v2;
}

/**
 * @param {Float32Array} mask
 * @param {number} width
 * @param {number} height
 * @returns {Float64Array}
 */
export function minimum_area_rect(mask, width, height) {
    const ptr0 = passArrayF32ToWasm0(mask, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.minimum_area_rect(ptr0, len0, width, height);
    var v2 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
    return v2;
}

/**
 * @param {Float32Array} mask
 * @param {number} width
 * @param {number} height
 * @param {number} scale_x
 * @param {number} scale_y
 * @returns {Float64Array}
 */
export function minimum_area_rect_scaled(mask, width, height, scale_x, scale_y) {
    const ptr0 = passArrayF32ToWasm0(mask, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.minimum_area_rect_scaled(ptr0, len0, width, height, scale_x, scale_y);
    var v2 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
    return v2;
}

/**
 * Rectangular morphology: operation 0 dilate, 1 erode, 2 close and 3 open.
 * @param {Uint8Array} mask
 * @param {number} width
 * @param {number} height
 * @param {number} radius
 * @param {number} operation
 * @returns {Uint8Array}
 */
export function morphology(mask, width, height, radius, operation) {
    const ptr0 = passArray8ToWasm0(mask, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.morphology(ptr0, len0, width, height, radius, operation);
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Return sheet width/height, content width/height and a note code.
 * @param {number} image_width
 * @param {number} image_height
 * @param {number} x0
 * @param {number} y0
 * @param {number} x1
 * @param {number} y1
 * @param {number} millimeters_per_pixel
 * @param {number} sheet_width
 * @param {number} sheet_height
 * @param {number} fit
 * @param {number} preset_width
 * @param {number} preset_height
 * @param {number} margin
 * @returns {Float64Array}
 */
export function plan_layout(image_width, image_height, x0, y0, x1, y1, millimeters_per_pixel, sheet_width, sheet_height, fit, preset_width, preset_height, margin) {
    const ret = wasm.plan_layout(image_width, image_height, x0, y0, x1, y1, millimeters_per_pixel, sheet_width, sheet_height, fit, preset_width, preset_height, margin);
    var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
    return v1;
}

/**
 * @param {Float64Array} points
 * @param {number} epsilon
 * @returns {Float64Array}
 */
export function simplify_polygon(points, epsilon) {
    const ptr0 = passArrayF64ToWasm0(points, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.simplify_polygon(ptr0, len0, epsilon);
    var v2 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
    return v2;
}

/**
 * @param {Float32Array} grey
 * @param {number} width
 * @param {number} height
 * @returns {Float32Array}
 */
export function sobel_magnitude(grey, width, height) {
    const ptr0 = passArrayF32ToWasm0(grey, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.sobel_magnitude(ptr0, len0, width, height);
    var v2 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v2;
}

/**
 * Trace foreground pixel boundaries as closed edge loops. Each loop is encoded as its
 * point count followed by x/y pairs, so several contours fit in one flat WASM array.
 * @param {Uint8Array} mask
 * @param {number} width
 * @param {number} height
 * @returns {Float64Array}
 */
export function trace_contours(mask, width, height) {
    const ptr0 = passArray8ToWasm0(mask, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.trace_contours(ptr0, len0, width, height);
    var v2 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
    return v2;
}
function __wbg_get_imports() {
    const import0 = {
        __proto__: null,
        __wbg_Error_92b29b0548f8b746: function(arg0, arg1) {
            const ret = Error(getStringFromWasm0(arg0, arg1));
            return ret;
        },
        __wbg___wbindgen_throw_344f42d3211c4765: function(arg0, arg1) {
            throw new Error(getStringFromWasm0(arg0, arg1));
        },
        __wbindgen_cast_0000000000000001: function(arg0, arg1) {
            // Cast intrinsic for `Ref(Slice(U8)) -> NamedExternref("Uint8Array")`.
            const ret = getArrayU8FromWasm0(arg0, arg1);
            return ret;
        },
        __wbindgen_init_externref_table: function() {
            const table = wasm.__wbindgen_externrefs;
            const offset = table.grow(4);
            table.set(0, undefined);
            table.set(offset + 0, undefined);
            table.set(offset + 1, null);
            table.set(offset + 2, true);
            table.set(offset + 3, false);
        },
    };
    return {
        __proto__: null,
        "./cropsize_imaging_core_bg.js": import0,
    };
}

const PrintMapFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_printmap_free(ptr, 1));
const RgbaFrameFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_rgbaframe_free(ptr, 1));

function _assertClass(instance, klass) {
    if (!(instance instanceof klass)) {
        throw new Error(`expected instance of ${klass.name}`);
    }
}

function getArrayF32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getFloat32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

function getArrayF64FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getFloat64ArrayMemory0().subarray(ptr / 8, ptr / 8 + len);
}

function getArrayU8FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint8ArrayMemory0().subarray(ptr / 1, ptr / 1 + len);
}

let cachedFloat32ArrayMemory0 = null;
function getFloat32ArrayMemory0() {
    if (cachedFloat32ArrayMemory0 === null || cachedFloat32ArrayMemory0.byteLength === 0) {
        cachedFloat32ArrayMemory0 = new Float32Array(wasm.memory.buffer);
    }
    return cachedFloat32ArrayMemory0;
}

let cachedFloat64ArrayMemory0 = null;
function getFloat64ArrayMemory0() {
    if (cachedFloat64ArrayMemory0 === null || cachedFloat64ArrayMemory0.byteLength === 0) {
        cachedFloat64ArrayMemory0 = new Float64Array(wasm.memory.buffer);
    }
    return cachedFloat64ArrayMemory0;
}

function getStringFromWasm0(ptr, len) {
    return decodeText(ptr >>> 0, len);
}

let cachedUint8ArrayMemory0 = null;
function getUint8ArrayMemory0() {
    if (cachedUint8ArrayMemory0 === null || cachedUint8ArrayMemory0.byteLength === 0) {
        cachedUint8ArrayMemory0 = new Uint8Array(wasm.memory.buffer);
    }
    return cachedUint8ArrayMemory0;
}

function passArray8ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 1, 1) >>> 0;
    getUint8ArrayMemory0().set(arg, ptr / 1);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArrayF32ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 4, 4) >>> 0;
    getFloat32ArrayMemory0().set(arg, ptr / 4);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArrayF64ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 8, 8) >>> 0;
    getFloat64ArrayMemory0().set(arg, ptr / 8);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function takeFromExternrefTable0(idx) {
    const value = wasm.__wbindgen_externrefs.get(idx);
    wasm.__externref_table_dealloc(idx);
    return value;
}

let cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
cachedTextDecoder.decode();
const MAX_SAFARI_DECODE_BYTES = 2146435072;
let numBytesDecoded = 0;
function decodeText(ptr, len) {
    numBytesDecoded += len;
    if (numBytesDecoded >= MAX_SAFARI_DECODE_BYTES) {
        cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
        cachedTextDecoder.decode();
        numBytesDecoded = len;
    }
    return cachedTextDecoder.decode(getUint8ArrayMemory0().subarray(ptr, ptr + len));
}

let WASM_VECTOR_LEN = 0;

let wasmModule, wasmInstance, wasm;
function __wbg_finalize_init(instance, module) {
    wasmInstance = instance;
    wasm = instance.exports;
    wasmModule = module;
    cachedFloat32ArrayMemory0 = null;
    cachedFloat64ArrayMemory0 = null;
    cachedUint8ArrayMemory0 = null;
    wasm.__wbindgen_start();
    return wasm;
}

async function __wbg_load(module, imports) {
    if (typeof Response === 'function' && module instanceof Response) {
        if (typeof WebAssembly.instantiateStreaming === 'function') {
            try {
                return await WebAssembly.instantiateStreaming(module, imports);
            } catch (e) {
                const validResponse = module.ok && expectedResponseType(module.type);

                if (validResponse && module.headers.get('Content-Type') !== 'application/wasm') {
                    console.warn("`WebAssembly.instantiateStreaming` failed because your server does not serve Wasm with `application/wasm` MIME type. Falling back to `WebAssembly.instantiate` which is slower. Original error:\n", e);

                } else { throw e; }
            }
        }

        const bytes = await module.arrayBuffer();
        return await WebAssembly.instantiate(bytes, imports);
    } else {
        const instance = await WebAssembly.instantiate(module, imports);

        if (instance instanceof WebAssembly.Instance) {
            return { instance, module };
        } else {
            return instance;
        }
    }

    function expectedResponseType(type) {
        switch (type) {
            case 'basic': case 'cors': case 'default': return true;
        }
        return false;
    }
}

function initSync(module) {
    if (wasm !== undefined) return wasm;


    if (module !== undefined) {
        if (Object.getPrototypeOf(module) === Object.prototype) {
            ({module} = module)
        } else {
            console.warn('using deprecated parameters for `initSync()`; pass a single object instead')
        }
    }

    const imports = __wbg_get_imports();
    if (!(module instanceof WebAssembly.Module)) {
        module = new WebAssembly.Module(module);
    }
    const instance = new WebAssembly.Instance(module, imports);
    return __wbg_finalize_init(instance, module);
}

async function __wbg_init(module_or_path) {
    if (wasm !== undefined) return wasm;


    if (module_or_path !== undefined) {
        if (Object.getPrototypeOf(module_or_path) === Object.prototype) {
            ({module_or_path} = module_or_path)
        } else {
            console.warn('using deprecated parameters for the initialization function; pass a single object instead')
        }
    }

    if (module_or_path === undefined) {
        module_or_path = new URL('cropsize_imaging_core_bg.wasm', import.meta.url);
    }
    const imports = __wbg_get_imports();

    if (typeof module_or_path === 'string' || (typeof Request === 'function' && module_or_path instanceof Request) || (typeof URL === 'function' && module_or_path instanceof URL)) {
        module_or_path = fetch(module_or_path);
    }

    const { instance, module } = await __wbg_load(await module_or_path, imports);

    return __wbg_finalize_init(instance, module);
}

export { initSync, __wbg_init as default };
